import React from "react";

import { IndexRoute, Route } from "react-router";
import auth from "./config/auth0-config";

import Container from "./pages/Container.react";
import AdminStore from "./stores/AdminStore";
import CurrentRoleStore from "./stores/CurrentRoleStore";
import App from "./pages/App.react";
import MainApp from "./pages/MainApp.react";
import ChooseOrgContainer from "./pages/ChooseOrgContainer.react";
import Login from "./pages/Login.react";
import AuthCallback from "./pages/AuthCallback.react";
import Logout from "./pages/Logout.react";
import Contact from "./pages/Contact.react";
import AccountSettings from "./pages/account/Settings.react";
import AccountPermissions from "./pages/account/Permissions.react";
import ModelBuilder from "./pages/ModelBuilder.react";
import Jobs from "./pages/Jobs.react";
import SavedJobs from "./pages/savedjobs/SavedJobs.react";
import Syncs from "./pages/Syncs.react";
import Files from "./pages/Files.react";
import Settings from "./pages/Settings.react";
import Support from "./pages/Support.react";
import Overview from "./pages/Overview.react";
import Tableau from "./pages/Tableau.react";
import Content from "./pages/Content.react";

import Exporter from "./pages/Exporter.react";

import Matching from "./pages/matching/Matching.react";
import MatchingStep1 from "./pages/matching/Step1.react";
import MatchingComplete from "./pages/matching/Complete.react";

import Admin from "./pages/admin/Admin.react";
import ManageUser from "./pages/admin/ManageUserContainer.react";
import NewUser from "./pages/admin/NewUserContainer.react";
import ResetPassword from "./pages/admin/ResetPassword.react";
import ResetSuccess from "./pages/admin/ResetSuccess.react";
import AdminOptions from "./pages/admin/AdminOptions.react";

import JobDetail from "./components/JobDetail.react";

import MyUniverses from "./pages/MyUniverses.react";
import CrossChannelNew from "./pages/crosschannel/CrossChannelNew.react";
import CrossChannelShow from "./pages/crosschannel/CrossChannelShow.react";
import CrossChannelIndex from "./pages/crosschannel/CrossChannelIndex.react";
import CrossChannelParametersForm from "./pages/crosschannel/CrossChannelParametersForm.react";
import CrossChannelComplete from "./pages/crosschannel/Complete.react";

import NewJob from "./pages/newjob/NewJob.react";
import Credentials from "./pages/credentials/Credentials.react";
import Resources from "./pages/credentials/Resources.react";

function requireAdministrativePrivileges(privilege, fallBackTo = "/") {
  return (nextState, replace) => {
    if (!AdminStore.getState()[privilege]) {
      replace(fallBackTo);
    }
  };
}

function requireNotViewer(nextState, replace, callback) {
  // TODO: for some reason, if the user does not need to select an org on the choose page,
  // CurrentRoleStore is empty now. There must not be anything forcing a load?
  if (CurrentRoleStore.isContentViewer()) {
    replace({ pathname: "content" });
  } else if (CurrentRoleStore.isViewer()) {
    replace({ pathname: "tableau" });
  }
  callback();
}

const handleAuthentication = (nextState, replace, callback) => {
  if (/access_token|id_token|error/.test(nextState.location.hash)) {
    auth.handleAuthentication();
  }
  callback();
};

export default (
  <Route path="/" component={Container}>
    <Route path="login" component={Login} />
    <Route path="logout" component={Logout} />
    <Route
      path="authcallback"
      component={AuthCallback}
      onEnter={handleAuthentication}
    />
    <Route component={App}>
      <Route path="choose" component={ChooseOrgContainer} />
      <Route path="contact" component={Contact} />
      <Route path="account">
        <Route path="settings" component={AccountSettings} />
        <Route path="permissions" component={AccountPermissions} />
      </Route>
      <Route component={MainApp}>
        <IndexRoute
          name="overview"
          component={Overview}
          onEnter={requireNotViewer}
        />
        <Route path="my-universes">
          <IndexRoute component={MyUniverses} />
          <Route path="" component={MyUniverses} />
          <Route path=":reportpart(/:viewpart)" component={MyUniverses} />
        </Route>

        <Route
          path="crosschannel"
          component={CrossChannelIndex}
          onEnter={requireNotViewer}
        />
        <Route
          path="crosschannel/new"
          component={CrossChannelNew}
          onEnter={requireNotViewer}
        >
          <Route
            path="parameters"
            name="parameters"
            component={CrossChannelParametersForm}
          />
          <Route
            path="complete"
            name="complete"
            component={CrossChannelComplete}
          />
          <IndexRoute component={CrossChannelParametersForm} />
        </Route>
        <Route
          path="crosschannel/:scenarioId"
          component={CrossChannelShow}
          onEnter={requireNotViewer}
        />

        <Route
          path="exporter"
          component={Exporter}
          onEnter={requireNotViewer}
        />
        <Route path="matching" component={Matching} onEnter={requireNotViewer}>
          <Route path="1" component={MatchingStep1} />
          <Route path="complete" component={MatchingComplete} />
          <IndexRoute component={MatchingStep1} />
        </Route>
        <Route
          path="models"
          component={ModelBuilder}
          onEnter={requireNotViewer}
        />

        <Route
          path="jobs/new"
          name="newjob"
          component={NewJob}
          onEnter={requireNotViewer}
        />
        <Route
          path="savedjobs(/:savedJobId(/:mode))"
          name="savedjobs"
          component={SavedJobs}
          onEnter={requireNotViewer}
        />
        <Route path="files" component={Files} onEnter={requireNotViewer} />
        <Route path="jobs" component={Jobs} onEnter={requireNotViewer}>
          <Route path=":jobId" component={JobDetail} />
        </Route>

        <Route path="syncs" component={Syncs} onEnter={requireNotViewer} />
        <Route
          path="settings"
          component={Settings}
          onEnter={requireNotViewer}
        />
        <Route path="support" component={Support} />
        <Route path="tableau">
          <IndexRoute component={Tableau} />
          <Route path="" component={Tableau} />
          <Route path=":reportpart(/:viewpart)" component={Tableau} />
        </Route>
        <Route path="content">
          <IndexRoute component={Content} />
          <Route path="" component={Content} />
          <Route path=":pageId" component={Content} />
        </Route>
        <Route path="credentials">
          <IndexRoute component={Credentials} />
          <Route
            path="resources"
            name="resources"
            component={Resources}
            onEnter={requireAdministrativePrivileges(
              "canAdministerResources",
              "/credentials"
            )}
          />
        </Route>
        <Route path="admin" component={Admin}>
          <IndexRoute component={AdminOptions} />
          {/* TODO: Remove this reset password -- no longer supported */}
          <Route
            path="password"
            name="reset-password"
            component={ResetPassword}
            onEnter={requireAdministrativePrivileges("canResetPassword")}
          />
          <Route path="success" name="reset-success" component={ResetSuccess} />
          <Route path="options" name="admin-options" component={AdminOptions} />
          <Route
            path="user/new"
            name="new-user"
            component={NewUser}
            onEnter={requireAdministrativePrivileges("canAdministerUsers")}
          />
          <Route
            path="user/:userId"
            name="manage-user"
            component={ManageUser}
            onEnter={requireAdministrativePrivileges("canAdministerUsers")}
          />
        </Route>
      </Route>
    </Route>
  </Route>
);
